import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {MailingsListRequest} from "crqlar-react-common/dist/models/marketing/mailingsList.request";
import type {MailingRecord} from "crqlar-react-common/dist/models/newsletter/mailingRecord";
import {MailingRecordService} from "crqlar-react-common/dist/services/newsletter/mailingRecord.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useMailingRecordFilterOptions} from "crqlar-react-common/dist/utils/hooks/integrations/useNewsletter";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    type ServerTableUrlStateOptions,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const MARKETING_STATISTICS_URL_STATE: ServerTableUrlStateOptions = {
    filterAliases: [{
        queryParam: 'profileId',
        field: 'profileIds',
        operator: DataTypeOperator.IN
    }]
};

const MarketingStatisticsList = () => {
    useTrackScreenVisit('system marketing statistics');

    const {t} = useTranslation();
    const navigate = useNavigate();
    const filterOptionsQuery = useMailingRecordFilterOptions();
    const columns = useMemo<ColumnDef<MailingRecord>[]>(() => [
        {
            id: 'profile',
            header: t('Profile'),
            accessorFn: mailingRecord => mailingRecord.profileEmail,
            cell: ({row}) => <Box>{row.original.profileEmail || '-'}</Box>,
            size: 240,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'mailingName',
            header: t('Mailing Name'),
            accessorFn: mailingRecord => mailingRecord.mailingName,
            cell: ({row}) => <Box>{row.original.mailingName || '-'}</Box>,
            size: 220,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'mailingDate',
            header: t('Mailing Date'),
            accessorFn: mailingRecord => mailingRecord.mailingExecutionDate,
            cell: ({row}) => <Box>{row.original.mailingExecutionDate || '-'}</Box>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'mailingExecutionDate'
            }
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: mailingRecord => mailingRecord.status,
            cell: ({row}) => (
                <Box>{row.original.status ? t(`Mailing Record Status ${row.original.status}`) : '-'}</Box>
            ),
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'links',
            header: t('Links'),
            accessorFn: mailingRecord => mailingRecord.links,
            cell: ({row}) => <Box>{row.original.links?.join(', ') || '-'}</Box>,
            size: 280,
            meta: {defaultActive: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: mailingRecord => mailingRecord.id,
            cell: ({row}) => <Box>{row.original.id || ''}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        }
    ], [t]);
    const controller = useServerTable({
        tableId: 'system-marketing-statistics',
        columns,
        getRowId: mailingRecord => mailingRecord.id!,
        initialSorting: [{id: 'id', desc: true}],
        urlState: MARKETING_STATISTICS_URL_STATE,
        queryFn: async ({pagination, filterRules, sorting}) => {
            const request: MailingsListRequest = {
                advancedFilter: filterRules.length ? {
                    filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
                } : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            };
            const profileIds = filterRules.find(rule => rule.field === 'profileIds')?.textValues;
            if (profileIds !== undefined) request.profileIds = profileIds;

            const page = await MailingRecordService.getAll(request);
            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <SettingsListPage title={t('Marketing statistics')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSort/>
                        <ServerTableFilter
                            options={filterOptionsQuery.data ?? []}
                            disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                        />
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default MarketingStatisticsList;
